/*  Private Device Vault — Web Crypto helpers.
    AES-GCM 256-bit for authenticated confidentiality; PBKDF2-SHA256 (200,000 iters)
    for key derivation from the user's PIN/passphrase. IVs are freshly generated
    per encrypt call; salt is generated once at onboarding and stored beside the
    ciphertext (needed to re-derive the key from PIN on the next launch).
    Never persist the derived CryptoKey — it lives only in runtime memory.  */

const AES_ALGO = "AES-GCM";
const AES_LENGTH = 256;
const IV_LENGTH = 12;   // 96-bit IV, standard for AES-GCM
const SALT_LENGTH = 16;
const PBKDF2_ITERATIONS = 200_000;
const KEY_USAGES = ["encrypt", "decrypt"];

function _u8ToB64(u8) {
  let s = "";
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}
function _b64ToU8(b64) {
  const s = atob(b64);
  const u8 = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) u8[i] = s.charCodeAt(i);
  return u8;
}

export function randomSalt() { return crypto.getRandomValues(new Uint8Array(SALT_LENGTH)); }
export function randomIv()   { return crypto.getRandomValues(new Uint8Array(IV_LENGTH)); }

/** Derive an AES-GCM key from a PIN/passphrase + salt. */
export async function deriveKey(pinOrPassphrase, salt) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw", enc.encode(pinOrPassphrase), { name: "PBKDF2" }, false, ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    keyMaterial,
    { name: AES_ALGO, length: AES_LENGTH },
    false,
    KEY_USAGES,
  );
}

/** Encrypt a JS object → { ciphertext, iv } (base64 strings). */
export async function encryptObject(key, obj) {
  const iv = randomIv();
  const enc = new TextEncoder();
  const plaintext = enc.encode(JSON.stringify(obj));
  const cipherBuf = await crypto.subtle.encrypt({ name: AES_ALGO, iv }, key, plaintext);
  return { ciphertext: _u8ToB64(new Uint8Array(cipherBuf)), iv: _u8ToB64(iv) };
}

/** Decrypt { ciphertext, iv } back into the original object. */
export async function decryptObject(key, payload) {
  const iv = _b64ToU8(payload.iv);
  const ct = _b64ToU8(payload.ciphertext);
  const plainBuf = await crypto.subtle.decrypt({ name: AES_ALGO, iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(plainBuf));
}

/** Generate a completely random AES-GCM key (used for one-off exports). */
export async function generateExportKey() {
  return crypto.subtle.generateKey({ name: AES_ALGO, length: AES_LENGTH }, true, KEY_USAGES);
}

/** Export a random key to raw bytes → base64 (for backup verification). */
export async function exportKeyRaw(key) {
  const raw = await crypto.subtle.exportKey("raw", key);
  return _u8ToB64(new Uint8Array(raw));
}

export const _internals = { _u8ToB64, _b64ToU8, PBKDF2_ITERATIONS, IV_LENGTH, SALT_LENGTH };
